import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Grid from '@mui/material/Grid';

import { Typography, Button, CaretIcon } from '@tkww/the-bash-frontend';
import { useDispatch } from 'react-redux';
import { articleTileClick } from 'state/modules/analytics';
import ArticleCard from 'components/ArticleCard/index.js';
import { Collapse, useTheme } from '@mui/material';
import SectionContainer from 'components/grid/SectionContainer';
import isEmpty from 'lodash.isempty';

const VISIBLE_ARTICLES = 4;
const MAX_VISIBLE_ARTICLES = 8;

const getArticleTitleStyles = (theme) => ({
  ...theme.typography.body1,
  ...theme.fontSizes[16],
  margin: 0,
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  overflow: 'hidden',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  color: theme.palette.grey[700],
  '&:hover': {
    textDecoration: 'none',
  },
  fallbacks: {
    // fallback for browsers that dont support -webkit-box
    display: 'block',
  },
});

export const RelatedArticles = ({ articles, title, titleAlign, analyticsProps }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [showMore, setShowMore] = useState(false);

  const handleArticleClick = ({ hed: articleName, slug: articleSlug }) => {
    let segmentProps = {
      articleName,
      articleSlug,
    };

    if (!isEmpty(analyticsProps)) {
      segmentProps = {
        ...segmentProps,
        ...analyticsProps,
      };
    }

    dispatch(articleTileClick({ ...segmentProps }));
  };

  if (!articles || articles.length <= 0) {
    return null;
  }

  return (
    <SectionContainer backgroundColor={theme.palette.grey[200]}>
      <Typography
        variant="h2"
        sx={{ margin: 0, paddingTop: 4, paddingBottom: 3, textAlign: titleAlign }}
      >
        {title}
      </Typography>
      <Grid container spacing={2} justifyContent="center">
        {articles.slice(0, VISIBLE_ARTICLES).map((article) => (
          <Grid item xs={12} md={6} lg={3} key={article.hed}>
            <ArticleCard
              sxTitle={getArticleTitleStyles}
              onClick={({ hed, slug }) => handleArticleClick({ hed, slug })}
              hed={article.hed}
              thumbSquareUrl={article.thumbSquareUrl}
              slug={article.slug}
            />
          </Grid>
        ))}
        {articles.length > VISIBLE_ARTICLES && (
          <>
            <Grid style={{ paddingTop: 0, paddingBottom: 0 }} item xs={12}>
              <Collapse in={showMore} unmountOnExit>
                <Grid container spacing={2} justifyContent="center">
                  {articles.slice(VISIBLE_ARTICLES, MAX_VISIBLE_ARTICLES).map((article) => (
                    <Grid item xs={12} md={6} lg={3} key={article.hed}>
                      <ArticleCard
                        sxTitle={getArticleTitleStyles}
                        onClick={({ hed, slug }) => handleArticleClick({ hed, slug })}
                        hed={article.hed}
                        thumbSquareUrl={article.thumbSquareUrl}
                        slug={article.slug}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Collapse>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                padding: 0,
                marginTop: '-28px',
                textAlign: 'center',
                marginBottom: '20px',
                '& p': {
                  textTransform: 'uppercase',
                  fontWeight: theme.typography.h5.fontWeight,
                  fontSize: 14,
                  lineHeight: '18px',
                },
                '& .icon': {
                  verticalAlign: 'middle',
                },
              }}
            >
              <Button type="tertiary" onClick={() => setShowMore((prev) => !prev)}>
                <Typography variant="body1">{showMore ? 'Show less' : 'Show more'}</Typography>
                <CaretIcon className="icon" direction={showMore ? 'top' : 'bottom'} />
              </Button>
            </Grid>
          </>
        )}
      </Grid>
    </SectionContainer>
  );
};

const relatedArticlesProptypes = {
  /**
   * @param {Object[]} articles - An array containing articles
   * @param {string} articles[].hed - The title of the article
   * @param {string} articles[].slug - The slug of the article (used for the href)
   * @param {string} articles[].thumbSquareUrl - The image of the article
   */
  articles: PropTypes.arrayOf(
    PropTypes.shape({
      hed: PropTypes.string.isRequired,
      slug: PropTypes.string.isRequired,
      thumbSquareUrl: PropTypes.string.isRequired,
    })
  ).isRequired,
  title: PropTypes.string.isRequired,
  /**
   * @param {string} titleAlign - The alignment of the title one of ['left', 'right', 'center', 'justify', 'inherit']
   */
  titleAlign: PropTypes.oneOf(['left', 'right', 'center', 'justify', 'inherit']),
  analyticsProps: PropTypes.shape({}),
};

RelatedArticles.defaultProps = {
  titleAlign: 'left',
  analyticsProps: null,
};

RelatedArticles.propTypes = relatedArticlesProptypes;

export default RelatedArticles;
